"use client";

import StyledGreenCheck from "@/app/assets/icons/styled-green-check";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/stories/accordian/accordian";
import { ChevronDown, Star, Wallet } from "lucide-react";
import Image from "next/image";
import { StocksAssets } from "@/app/stocks/icm 3.0/stocks-assets";
import BrokingChargeBreakdown from "@/app/stocks/icm 3.0/shared/brocking-charge-breakdown";

const pricingPlans = [
    {
        badge: "Pay as you go",
        badgeColor:
            "rounded-lg border border-[#FDE7D7] bg-[#FEF5EE] text-brand-primary-500",
        BadgeIcon: Wallet,
        title: "Per Trade Plan",
        subtitle: "Flat ₹9 per executed order — all segments",
        features: [
            "All segments: Equity, F&O, MCX, NCDEX",
            "Full product suite access",
            "No monthly commitment",
            "No brokerage on zero-trade months",
        ],
        price: "9",
        priceLabel: "Per order",
    },
    {
        badge: "Unlimited Trading",
        badgeColor: "bg-brand-primary-500 text-white",
        BadgeIcon: Star,
        title: "Monthly Brokerage Plan",
        subtitle: "₹0 brokerage on every trade",
        features: [
            "Unlimited trades, flat monthly cap",
            "Trade Cash, Futures & Options",
            "Full product suite access",
            "Trade unlimited in NSE & BSE F&O",
        ],
        price: "999",
        priceLabel: "Per month",
    },
] as const;

export default function WhatStockoProvides() {
    return (
        <section
            aria-labelledby="pricing-heading"
            className="mt-[60px] md:mt-[120px] w-full px-5 pb-10 md:px-[120px] md:pb-0"
        >
            <div className="mx-auto box-border flex w-full max-w-[1200px] flex-col items-center gap-10 rounded-2xl bg-[#FDE7D7] px-5 py-10 md:px-[74px] md:py-[42px]">
                <header className="flex w-full flex-col items-center gap-2 text-center">
                    <h2
                        id="pricing-heading"
                        className="text-center text-2xl font-bold leading-8 tracking-[var(--typography-font-letter-spacing-xs)] text-[var(--colors-text-header)] md:text-[length:var(--typography-font-size-11xl)] md:leading-[var(--typography-font-line-height-11xl)]"
                    >
                        Simple. Flat. No Surprises.
                    </h2>
                    <p className="text-center text-base font-regular leading-[var(--typography-font-line-height-m)] tracking-[var(--typography-font-letter-spacing-xs)] text-[var(--colors-text-sub-header)] md:text-[length:var(--typography-font-size-xl)] md:leading-[var(--typography-font-line-height-xl)]">
                        Pay only when you trade. No prior commitments, no platform charges, no
                        hidden fees. Ever.
                    </p>
                </header>

                <div className="flex w-full flex-col items-center gap-5 md:flex-row md:justify-between md:gap-6">
                    {pricingPlans.map((plan) => (
                        <article
                            key={plan.title}
                            className="relative flex h-auto w-full max-w-[346px] flex-col overflow-hidden rounded-2xl bg-white shadow-[0px_1px_1px_0px_rgba(0,0,0,0.25)] md:h-[382px] md:w-[469px] md:max-w-none md:shrink-0 md:p-6"
                        >
                            <Image
                                src={StocksAssets.pricingCardCorner}
                                alt=""
                                width={235}
                                height={253}
                                className="pointer-events-none absolute bottom-0 right-0 z-0 h-[58%] w-auto max-w-[68%] object-contain object-right-bottom"
                                aria-hidden
                            />
                            <div className="relative z-10 flex flex-col gap-6 px-5 pb-[194px] pt-8 md:gap-6 md:p-0 md:pb-0">
                                <div className="flex flex-col gap-4">
                                    <div
                                        className={`inline-flex w-fit items-center gap-1.5 rounded-lg px-3 py-1 text-sm font-medium leading-5 md:leading-6 ${plan.badgeColor}`}
                                    >
                                        <plan.BadgeIcon
                                            className="size-4"
                                            aria-hidden
                                        />
                                        {plan.badge}
                                    </div>

                                    <div className="flex flex-col gap-1">
                                        <h3 className="text-2xl font-semibold leading-8 text-[var(--colors-text-header)]">
                                            {plan.title}
                                        </h3>
                                        <p className="max-w-[242px] md:max-w-none text-base font-medium leading-[22px] text-[var(--colors-neutral-brand-black,#0f1016)]">
                                            {plan.subtitle}
                                        </p>
                                    </div>
                                </div>

                                <ul className="flex flex-col gap-3">
                                    {plan.features.map((feature) => (
                                        <li
                                            key={feature}
                                            className="flex items-center gap-2.5 text-sm font-regular leading-5 text-[var(--colors-text-sub-header)]"
                                        >
                                            <StyledGreenCheck className="shrink-0" aria-hidden />
                                            <span>{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="absolute bottom-[52px] right-6 z-10 text-right md:bottom-6 md:right-6">
                                <div className="flex items-start justify-end">
                                    <span className="mt-3.5 h-[44px] w-[21px] text-[36px] font-semibold leading-[44px] tracking-[0px] text-[var(--colors-text-header)]">
                                        ₹
                                    </span>
                                    <span className="text-[72px] font-bold leading-[88px] tracking-[0px] text-[var(--colors-text-header)]">
                                        {plan.price}
                                    </span>
                                </div>
                                <p className="-mt-1.5 text-[20px] font-medium leading-[26px] tracking-[0px] text-[var(--colors-text-sub-header)]">
                                    {plan.priceLabel}
                                </p>
                            </div>
                        </article>
                    ))}
                </div>

                <div className="mt-[22px] w-full min-w-0 max-w-full md:mt-[22px]">
                    <Accordion
                        type="single"
                        collapsible
                        className="w-full min-w-0 max-w-full"
                    >
                        <AccordionItem
                            value="charge-breakdown"
                            className="w-full min-w-0 max-w-full !border-none"
                        >
                            <AccordionTrigger
                                showIcon={false}
                                className="ml-auto inline-flex w-fit flex-none items-center justify-center gap-s2 rounded-lg !border-none !py-0 font-[family-name:var(--typography-font-family-label)] text-[length:var(--typography-font-size-s)] font-medium leading-[var(--typography-font-line-height-s)] tracking-[var(--typography-font-letter-spacing-xs)] text-[var(--colors-text-link-text)] underline decoration-solid decoration-[12%] underline-offset-[25%] hover:underline [&[data-state=open]>svg]:rotate-180"
                            >
                                View Detailed Charge Breakdown
                                <ChevronDown
                                    size={16}
                                    className="shrink-0 transition-transform duration-200"
                                />
                            </AccordionTrigger>
                            <AccordionContent className="w-full min-w-0 max-w-full pt-3 md:pt-4">
                                <BrokingChargeBreakdown />
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                </div>
            </div>
        </section>
    );
}
